import { useMutation, useQueryClient, type UseMutationResult } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { useProfile } from "@/hooks/useProfile";
import { useToast } from "@/hooks/use-toast";
import { STAY_PLAN_TABLES, inheritCarryover } from "@/lib/daycare-hotel/stayPlans";
import {
  buildChargePeriod,
  buildContractInsert,
  type ContractInsert,
  type StayPlanContractDraft,
  type StayPlanContractPlanInfo,
} from "./stayPlanContractDraft";

export interface ContractStayPlanInput {
  draft: StayPlanContractDraft;
  petId: string;
  ownerId: string | null;
  deferInitialCharge?: boolean;
}

export interface ContractStayPlanResult {
  contractId: string;
  saleId: string;
  planName: string;
  planPrice: number;
}

/* eslint-disable @typescript-eslint/no-explicit-any */
/** Monta o contrato como o banco o grava: plano, reposição herdada e faixa de preço. */
export async function prepareStayPlanContract({
  draft,
  petId,
  ownerId,
  clinicId,
}: ContractStayPlanInput & { clinicId: string }): Promise<{
  contractInsert: ContractInsert;
  planInfo: StayPlanContractPlanInfo;
  packageDates: string[];
}> {
  const tables = STAY_PLAN_TABLES[draft.module];

  const { data: plan, error: planError } = await (supabase as any)
    .from(tables.plans)
    .select(
      `id, name, price, kind, ` +
      `day_package:${tables.dayPackages}(included_days), ` +
      `recurring_rule:${tables.recurringRules}(usage_rule, usage_limit${draft.module === "daycare" ? ", week_interval" : ""})`,
    )
    .eq("id", draft.planId)
    .eq("clinic_id", clinicId)
    .eq("is_active", true)
    .single();
  if (planError) throw planError;

  const { data: previous } = await (supabase as any)
    .from(tables.contracts)
    .select("carryover_units, carryover_expires_on")
    .eq("clinic_id", clinicId)
    .eq("pet_id", petId)
    .neq("status", "active")
    .order("starts_on", { ascending: false })
    .limit(1)
    .maybeSingle();

  const planInfo = {
    name: plan.name as string,
    price: Number(plan.price),
    kind: plan.kind as "day_package" | "recurring",
    includedDays: plan.day_package?.included_days ?? null,
    usageRule: plan.recurring_rule?.usage_rule ?? null,
    usageLimit: plan.recurring_rule?.usage_limit ?? null,
    weekInterval: Number(plan.recurring_rule?.week_interval ?? 1) === 2 ? (2 as const) : (1 as const),
  };

  // A faixa de preço do hotel depende de o PET ter matrícula de creche ativa —
  // dado que o modal de contratação não conhece. Resolver aqui, onde o petId
  // existe, evita congelar a faixa errada no contrato.
  let priceTier = draft.priceTier;
  if (draft.module === "hotel" && planInfo.kind === "day_package" && priceTier === undefined) {
    const { data: daycareContracts } = await (supabase as any)
      .from("daycare_plan_contracts")
      .select("id, terms_snapshot")
      .eq("clinic_id", clinicId)
      .eq("pet_id", petId)
      .eq("status", "active");
    const hasMatricula = ((daycareContracts ?? []) as Array<{ terms_snapshot?: { usage_rule?: string | null } }>).some(
      (row) => Boolean(row.terms_snapshot?.usage_rule),
    );
    priceTier = hasMatricula ? "with_daycare_plan" : "standard";
  }

  const contractInsert = buildContractInsert({
    draft: { ...draft, priceTier },
    plan: planInfo,
    clinicId,
    petId,
    ownerId,
    inheritedCarryover: inheritCarryover(
      previous
        ? { carryoverUnits: Number(previous.carryover_units ?? 0), carryoverExpiresOn: previous.carryover_expires_on ?? null }
        : null,
      draft.startsOn,
    ),
  });

  const packageDates = planInfo.kind === "day_package" ? draft.packageDates ?? [] : [];
  return { contractInsert, planInfo, packageDates };
}

export async function contractStayPlan({
  draft,
  petId,
  ownerId,
  deferInitialCharge = false,
  clinicId,
}: ContractStayPlanInput & { clinicId: string }): Promise<ContractStayPlanResult> {
  const tables = STAY_PLAN_TABLES[draft.module];
  const { contractInsert, planInfo, packageDates } = await prepareStayPlanContract({ draft, petId, ownerId, clinicId });

  const { data: contract, error: contractError } = await (supabase as any)
    .from(tables.contracts).insert(contractInsert).select("id").single();
  if (contractError) throw contractError;

  // Matrícula: os dias fixos moram em tabela própria e a RPC gera as diárias
  // do ciclo corrente. Plano sem dias (per_month, pacote) não entra.
  const fixedWeekdays = draft.module === "daycare" && planInfo.usageRule === "per_week" ? draft.fixedWeekdays ?? [] : [];

  if (fixedWeekdays.length > 0) {
    const { error: daysError } = await (supabase as any)
      .from("daycare_plan_contract_days")
      .insert(fixedWeekdays.map((weekday) => ({ contract_id: contract.id, weekday })));
    if (daysError) throw daysError;

    const { error: materializeError } = await (supabase as any).rpc("materialize_daycare_plan_cycle", {
      p_contract_id: contract.id,
      p_from: draft.startsOn,
    });
    if (materializeError) throw materializeError;
  }

  // Pacote: as datas escolhidas viram diárias com hold já na compra.
  if (packageDates.length > 0) {
    const { error: datesError } = await (supabase as any).rpc("materialize_stay_package_dates", {
      p_module: draft.module,
      p_contract_id: contract.id,
      p_dates: packageDates,
    });
    if (datesError) throw datesError;
  }

  if (deferInitialCharge) {
    return { contractId: contract.id, saleId: null, planName: planInfo.name, planPrice: planInfo.price };
  }

  // A mensalidade tem venda própria: nunca entra na comanda da estadia.
  const { data: sale, error: saleError } = await (supabase as any)
    .from("sales")
    .insert({
      sale_number: `V${Date.now()}`,
      clinic_id: clinicId,
      pet_id: petId,
      owner_id: ownerId,
      status: "ABERTA",
      subtotal: planInfo.price,
      total: planInfo.price,
    })
    .select("id")
    .single();
  if (saleError) throw saleError;

  const { error: itemError } = await (supabase as any).from("sale_items").insert({
    sale_id: sale.id,
    item_name: planInfo.name,
    item_id: null,
    quantity: 1,
    unit_price: planInfo.price,
    line_total: planInfo.price,
  });
  if (itemError) throw itemError;

  const period = buildChargePeriod({
    kind: planInfo.kind,
    startsOn: contractInsert.starts_on,
    firstDueOn: contractInsert.first_due_on,
    billingTiming: draft.billingTiming,
    packageDates,
  });
  const dueOn = period.dueOn;

  const { error: chargeError } = await (supabase as any).from(tables.charges).insert({
    contract_id: contract.id,
    sale_id: sale.id,
    due_on: dueOn,
    period_starts_on: period.periodStartsOn,
    period_ends_on: period.periodEndsOn,
  });
  if (chargeError) throw chargeError;

  return { contractId: contract.id, saleId: sale.id, planName: planInfo.name, planPrice: planInfo.price };
}
/* eslint-enable @typescript-eslint/no-explicit-any */

export function useContractStayPlan(): UseMutationResult<ContractStayPlanResult, unknown, ContractStayPlanInput> {
  const { data: profile } = useProfile();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation("daycareHotel");

  return useMutation({
    mutationFn: async (input: ContractStayPlanInput) => {
      if (!profile?.current_clinic_id) throw new Error("no_clinic_selected");
      return contractStayPlan({ ...input, clinicId: profile.current_clinic_id });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["stay-plan-contracts"] });
      queryClient.invalidateQueries({ queryKey: ["stay-plan-contracts-for-reservation"] });
      queryClient.invalidateQueries({ queryKey: ["daycare-hotel"] });
      toast({ title: t("planContract.created") });
    },
    onError: () => {
      toast({ variant: "destructive", title: t("planContract.createFailed") });
    },
  });
}
